import type { ComponentProps } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import { cn } from '@/lib/utils.js';

/*
 * shadcn/ui's dialog on Radix, re-authored against this project's tokens.
 *
 * Two things differ from the stock component, both on purpose:
 *
 *   1. Centring is done by the overlay's grid, not by `top-1/2 left-1/2` plus
 *      `-translate-1/2` on the content. Stock shadcn combines that resting
 *      translate with `zoom-in-95`, and tw-animate-css's `enter` keyframe
 *      writes a whole `transform`, wiping the translate out for the duration of
 *      the animation — so the panel slides in from the corner. Centring in the
 *      layout leaves `transform` entirely to the animation.
 *   2. The panel therefore animates opacity and scale only. Nothing here
 *      touches a layout property, and the blanket prefers-reduced-motion rule
 *      in global.css collapses both.
 */

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export function DialogOverlay({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-(--color-scrim) p-(--space-4)',
        'duration-(--duration-fast) ease-(--ease-out-expo)',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

export function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & { showCloseButton?: boolean }) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay>
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            'relative w-full max-w-[32rem] rounded-(--radius-lg) border border-rule',
            'bg-card p-0 text-foreground shadow-(--shadow-overlay)',
            'duration-(--duration-normal) ease-(--ease-out-expo)',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            className,
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              aria-label="Close"
              className={cn(
                'absolute top-(--space-3) right-(--space-3) grid size-6 place-items-center',
                'rounded-(--radius-sm) text-ink-subtle',
                'transition-[background-color,color] duration-(--duration-fast) ease-(--ease-out-expo)',
                'hover:bg-surface-hover hover:text-ink',
                'disabled:pointer-events-none',
              )}
            >
              <XIcon className="size-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  );
}

export function DialogHeader({ className, ...props }: ComponentProps<'header'>) {
  return (
    <header
      data-slot="dialog-header"
      className={cn('border-b border-rule px-(--space-5) py-(--space-3)', className)}
      {...props}
    />
  );
}

export function DialogBody({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-body"
      className={cn('px-(--space-5) py-(--space-4)', className)}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex flex-col-reverse justify-end gap-(--space-2) px-(--space-5) pb-(--space-4) sm:flex-row',
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-[length:var(--text-lead)] font-semibold', className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-[length:var(--text-small)] text-ink-muted', className)}
      {...props}
    />
  );
}
